import moment from 'moment'

import { payments as PAYMENTS, rentals as RENTALS } from '@homevest/utils'

const { PAYMENT_CATEGORIES, PAYMENT_STATUSES } = PAYMENTS
const { RENTAL_STATUS } = RENTALS

type LedgerUser = {
  id: string
  first_name?: string | null
  last_name?: string | null
}

type LedgerLiabilityType = {
  id: string
  name: string
  payment_category?: string | null
}

type LedgerPaymentInput = {
  amount: number
  available_on?: string | null
  category: string
  created_at?: string | null
  id: string
  initiated_at?: string | null
  liability_type: LedgerLiabilityType
  lifecycle?: string
  note?: string | null
  payment_date?: string | null
  payment_method?: string | null
  refund_reason?: string | null
  status: string
  stripe_charge_id?: string | null
  stripe_payment_intent_id?: string | null
  user?: LedgerUser
  buildium_transaction?: {
    buildium_id: number
  } | null
}

type LedgerRentalLiabilityInput = {
  amount: number
  buildium_id?: number | null
  date: string
  id: string
  liability_type: LedgerLiabilityType
  note?: string | null
  resource_type?: string | null
}

type LedgerRentalCreditInput = {
  amount: number
  buildium_id?: number | null
  date: string
  id: string
  liability_type: LedgerLiabilityType
  note?: string | null
  type?: string | null
}

type LedgerCashoutAdjustmentInput = {
  amount: number
  date: string
  id: string
  liability_type_id: string
  liability_type: LedgerLiabilityType
  type: string
}

export type LedgerItem = {
  amount: number
  category?: string | null
  liabilityLabel: string
  date: string
  id: string
  status: string
  type: typeof PAYMENT_TYPE | typeof LIABILITY_TYPE | typeof CREDIT_TYPE | typeof ADJUSTMENT_TYPE
  availableOn?: string | null
  user?: LedgerUser
  lifecycle?: string | null
  initiatedAt?: string | null
  paymentMethod?: string | null
  note?: string | null
  refundReason?: string | null
  liabilityTypeId: string
  stripeId?: string | null
  buildiumId?: number | null
  paymentSource?: 'buildium' | 'stripe'
  creditType?: string | null
  resourceType?: string | null
}

export type LedgerItemWithBalance = LedgerItem & { balance: number }

const PAYMENT_TYPE = 'Payment' as const
const LIABILITY_TYPE = 'Charge' as const
const CREDIT_TYPE = 'Credit' as const
const ADJUSTMENT_TYPE = 'Adjustment' as const
const SCHEDULED_RENTAL_LIABILITY_RESOURCE_TYPE = 'scheduled_rental_liabilities'

export const canManuallyCorrectLedgerItem = ({
  resourceType,
  type
}: Pick<LedgerItem, 'resourceType' | 'type'>) =>
  !resourceType ||
  (type === LIABILITY_TYPE && resourceType !== SCHEDULED_RENTAL_LIABILITY_RESOURCE_TYPE)

export const canScheduleRentalCharge = ({
  canCreateLiabilities,
  isLedgerLocked,
  rentalStatus
}: {
  canCreateLiabilities: boolean
  isLedgerLocked: boolean
  rentalStatus: string
}) => canCreateLiabilities && !isLedgerLocked && rentalStatus === RENTAL_STATUS.ACTIVE

export const getLedgerItemsWithBalance = (
  payments: LedgerPaymentInput[],
  rentalLiabilities: LedgerRentalLiabilityInput[],
  rentalCredits: LedgerRentalCreditInput[],
  adjustments?: LedgerCashoutAdjustmentInput[]
) => {
  const ledgerData = [
    ...payments.map(
      ({
        amount,
        available_on: availableOn,
        category,
        created_at: createdAt,
        id,
        initiated_at: initiatedAt,
        liability_type: liabilityType,
        lifecycle,
        note,
        payment_date: paymentDate = '',
        payment_method: paymentMethod,
        refund_reason: refundReason,
        status,
        stripe_charge_id: stripeChargeId,
        stripe_payment_intent_id: stripePaymentIntentId,
        user,
        buildium_transaction
      }) => ({
        amount,
        availableOn,
        category,
        date: paymentDate || moment(createdAt).format('YYYY-MM-DD'),
        id,
        initiatedAt,
        liabilityLabel: liabilityType.name,
        liabilityTypeId: liabilityType.id,
        lifecycle,
        note,
        paymentMethod,
        refundReason,
        status,
        stripeId: stripeChargeId || stripePaymentIntentId,
        type: PAYMENT_TYPE,
        user,
        buildiumId: buildium_transaction?.buildium_id,
        paymentSource: buildium_transaction ? ('buildium' as const) : ('stripe' as const)
      })
    ),
    ...rentalLiabilities.map(
      ({ amount, date, id, liability_type, note, buildium_id, resource_type }) => ({
        amount,
        category: liability_type.payment_category,
        date,
        id,
        liabilityLabel: liability_type.name,
        liabilityTypeId: liability_type.id,
        note,
        status: '',
        type: LIABILITY_TYPE,
        buildiumId: buildium_id,
        resourceType: resource_type
      })
    ),
    ...rentalCredits.map(
      ({ amount, date, id, note, liability_type, type: creditType, buildium_id }) => ({
        amount,
        category: liability_type.payment_category,
        liabilityLabel: liability_type.name,
        liabilityTypeId: liability_type.id,
        date,
        id,
        note,
        status: 'succeeded',
        type: CREDIT_TYPE,
        creditType,
        buildiumId: buildium_id
      })
    ),
    ...(adjustments ?? []).map(({ amount, date, id, liability_type_id, liability_type, type }) => ({
      amount,
      category: liability_type.payment_category,
      liabilityLabel: `${type} ${liability_type.name}`,
      liabilityTypeId: liability_type_id,
      date,
      id,
      note: '',
      status: 'succeeded',
      type: ADJUSTMENT_TYPE
    }))
  ]

  const sortedWithData = ledgerData
    .sort((a, b) => {
      const daysDiff = moment(a.date).diff(b.date, 'days')

      if (daysDiff !== 0) {
        return daysDiff
      } else if (a.type === LIABILITY_TYPE) {
        return -1
      } else {
        return 1
      }
    })
    .reduce(
      (
        { ledgerDataWithBalances, balance, investmentToDate, rentPaidToDate, currentBalance },
        ld
      ) => {
        if (
          ([PAYMENT_TYPE, ADJUSTMENT_TYPE, CREDIT_TYPE] as string[]).includes(ld.type) &&
          ld.category !== PAYMENT_CATEGORIES.INITIAL_PAYMENT &&
          [PAYMENT_STATUSES.SUCCEEDED, PAYMENT_STATUSES.PROCESSING].includes(ld.status as any)
        ) {
          balance -= ld.amount

          // only update current balance if the date is today or in the past
          if (moment(ld.date).isSameOrBefore(moment(), 'day')) {
            currentBalance -= ld.amount
          }

          if (ld.category === PAYMENT_CATEGORIES.RENT) {
            rentPaidToDate += ld.amount
          } else if (ld.category === PAYMENT_CATEGORIES.OPTION_PREMIUM) {
            investmentToDate += ld.amount
          }
        } else if (ld.type === 'Charge') {
          balance += ld.amount

          // only update current balance if the date is today or in the past
          if (moment(ld.date).isSameOrBefore(moment(), 'day')) {
            currentBalance += ld.amount
          }
        }

        const dataWithBalance = {
          ...ld,
          balance
        }

        ledgerDataWithBalances.push(dataWithBalance)

        return {
          ledgerDataWithBalances,
          balance,
          investmentToDate,
          rentPaidToDate,
          currentBalance
        }
      },
      {
        ledgerDataWithBalances: [] as LedgerItemWithBalance[],
        balance: 0,
        investmentToDate: 0,
        rentPaidToDate: 0,
        currentBalance: 0
      }
    )

  sortedWithData.ledgerDataWithBalances.reverse()
  return sortedWithData
}
